import { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import PageHeader from '@/components/PageHeader.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import {
  createLocalidad,
  deactivateLocalidad,
  getGeografiaError,
  listLocalidades,
  listProvincias,
  updateLocalidad,
} from '@/services/geografia.js';

const blankForm = { id: null, provincia_id: '', nombre: '', codigo_localidad: '', activo: true };
const inputClass = 'h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.88)] px-3 text-sm text-[rgb(var(--fg))] outline-none transition focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:bg-[rgba(var(--subpanel),0.75)]';
const panelClass = 'rounded-2xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.92)] p-3 shadow-[0_10px_30px_rgba(var(--shadow),0.12)] md:p-4';

export default function DiccionarioLocalidadesPage() {
  const [provincias, setProvincias] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: '', provincia_id: '', activo: '1' });
  const [form, setForm] = useState(blankForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadProvincias = async () => {
    const data = await listProvincias();
    setProvincias(Array.isArray(data) ? data : []);
  };

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listLocalidades({
        q: filters.q || undefined,
        provincia_id: filters.provincia_id || undefined,
        activo: filters.activo,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      setRows([]);
      await Swal.fire('Error', getGeografiaError(error, 'No se pudieron cargar las localidades'), 'error');
    } finally {
      setLoading(false);
    }
  }, [filters.activo, filters.provincia_id, filters.q]);

  useEffect(() => {
    loadProvincias().catch((error) => Swal.fire('Error', getGeografiaError(error, 'No se pudieron cargar las provincias'), 'error'));
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const resetForm = () => setForm(blankForm);

  const handleEdit = (row) => {
    setForm({
      id: row.id,
      provincia_id: String(row.provincia_id),
      nombre: row.nombre || '',
      codigo_localidad: row.codigo_localidad || '',
      activo: Boolean(row.activo),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!form.provincia_id || !form.nombre.trim() || !form.codigo_localidad.trim()) {
      await Swal.fire('Atención', 'Provincia, nombre y código de localidad son obligatorios.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        provincia_id: Number(form.provincia_id),
        nombre: form.nombre.trim(),
        codigo_localidad: form.codigo_localidad.trim().toUpperCase(),
        activo: form.activo,
      };
      if (form.id) {
        await updateLocalidad(form.id, payload);
      } else {
        await createLocalidad(payload);
      }
      resetForm();
      await loadRows();
      await Swal.fire('Listo', form.id ? 'Localidad actualizada correctamente.' : 'Localidad creada correctamente.', 'success');
    } catch (error) {
      await Swal.fire('Error', getGeografiaError(error, 'No se pudo guardar la localidad'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (row) => {
    if (!row.activo) return;
    const result = await Swal.fire({
      title: '¿Desactivar localidad?',
      text: 'No se borrará el historial ni las referencias existentes.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Desactivar',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;

    try {
      await deactivateLocalidad(row.id);
      if (String(form.id) === String(row.id)) resetForm();
      await loadRows();
      await Swal.fire('Listo', 'Localidad desactivada correctamente.', 'success');
    } catch (error) {
      await Swal.fire('Error', getGeografiaError(error, 'No se pudo desactivar la localidad'), 'error');
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-3 px-2 py-2 md:px-3 md:py-3">
      <PageHeader
        title="Diccionario de localidades"
        subtitle="Maestro geográfico utilizado en direcciones, sucursales y rutas."
      />

      <section className={`${panelClass} grid grid-cols-12 gap-3`}>
        <div className="col-span-12">
          <h2 className="text-sm font-semibold text-[rgb(var(--fg))]">{form.id ? `Editar localidad #${form.id}` : 'Nueva localidad'}</h2>
        </div>
        <div className="col-span-12 md:col-span-4">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Provincia</label>
          <select className={inputClass} value={form.provincia_id} onChange={(event) => setForm((current) => ({ ...current, provincia_id: event.target.value }))}>
            <option value="">Seleccionar provincia</option>
            {provincias.map((provincia) => <option key={provincia.id} value={provincia.id}>{provincia.nombre}</option>)}
          </select>
        </div>
        <div className="col-span-12 md:col-span-5">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Nombre</label>
          <input className={inputClass} value={form.nombre} onChange={(event) => setForm((current) => ({ ...current, nombre: event.target.value }))} placeholder="Ej. San Miguel de Tucumán" />
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Código</label>
          <input className={inputClass} value={form.codigo_localidad} onChange={(event) => setForm((current) => ({ ...current, codigo_localidad: event.target.value.toUpperCase() }))} placeholder="Ej. TUC-SMT" />
        </div>
        <div className="col-span-12">
          <label className="inline-flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
            <input type="checkbox" checked={form.activo} onChange={(event) => setForm((current) => ({ ...current, activo: event.target.checked }))} />
            Localidad activa
          </label>
        </div>
        <div className="col-span-12">
          <StickyActions onCancel={resetForm} onSubmit={handleSubmit} isSubmitting={saving} primaryLabel={form.id ? 'Guardar cambios' : 'Crear localidad'} secondaryLabel="Limpiar" />
        </div>
      </section>

      <section className={`${panelClass} grid grid-cols-12 gap-3`}>
        <div className="col-span-12 md:col-span-5">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Buscar</label>
          <input className={inputClass} value={filters.q} onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))} placeholder="Nombre, código o provincia" />
        </div>
        <div className="col-span-12 md:col-span-4">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Provincia</label>
          <select className={inputClass} value={filters.provincia_id} onChange={(event) => setFilters((current) => ({ ...current, provincia_id: event.target.value }))}>
            <option value="">Todas las provincias</option>
            {provincias.map((provincia) => <option key={provincia.id} value={provincia.id}>{provincia.nombre}</option>)}
          </select>
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Estado</label>
          <select className={inputClass} value={filters.activo} onChange={(event) => setFilters((current) => ({ ...current, activo: event.target.value }))}>
            <option value="1">Activas</option>
            <option value="0">Inactivas</option>
            <option value="">Todas</option>
          </select>
        </div>
        <div className="col-span-12">
          {loading ? <div className="py-8 text-center text-sm text-[rgba(var(--muted),0.88)]">Cargando localidades...</div> : rows.length === 0 ? (
            <EmptyState title="Sin localidades" description="Creá la primera localidad o ajustá los filtros de búsqueda." />
          ) : (
            <div className="overflow-auto rounded-2xl border border-[rgba(var(--border),0.55)]">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-[rgba(var(--subpanel),0.58)] text-[rgba(var(--muted),0.95)]"><tr><th className="px-3 py-2">Provincia</th><th className="px-3 py-2">Localidad</th><th className="px-3 py-2">Código</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2 text-right">Acciones</th></tr></thead>
                <tbody>{rows.map((row) => <tr key={row.id} className="border-t border-[rgba(var(--border),0.42)]"><td className="px-3 py-2">{row.provincia_nombre}</td><td className="px-3 py-2 font-medium">{row.nombre}</td><td className="px-3 py-2">{row.codigo_localidad}</td><td className="px-3 py-2"><span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${row.activo ? 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--brand))]' : 'bg-[rgba(var(--muted),0.14)] text-[rgba(var(--muted),0.95)]'}`}>{row.activo ? 'Activa' : 'Inactiva'}</span></td><td className="px-3 py-2"><div className="flex justify-end gap-2"><button type="button" className="rounded-lg border border-[rgb(var(--border))] px-2 py-1 text-xs" onClick={() => handleEdit(row)}>Editar</button>{row.activo ? <button type="button" className="rounded-lg border border-[rgb(var(--border))] px-2 py-1 text-xs" onClick={() => handleDeactivate(row)}>Desactivar</button> : null}</div></td></tr>)}</tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
